import { z } from 'zod';
/**
 * Zod-схемы для котировок и контрактов — ПП РФ № 1317 п.3 е).
 *
 * Flow: менеджер выставляет Quote к Order (PROPOSED) → клиент акцептует
 * (ACCEPTED) или отклоняет (REJECTED). При акцепте автоматически создаётся
 * Contract и Order переходит в ACCEPTED.
 */
export const quoteStatusSchema = z.enum(['PROPOSED', 'ACCEPTED', 'REJECTED', 'EXPIRED', 'REVOKED']);
/**
 * Ставки НДС (для поручения, котировки, требований оплаты).
 *
 * «Без НДС» (не облагается) кодируется значением -1 — отличается от ставки 0%
 * (облагается по нулевой ставке). При расчёте суммы НДС отрицательная ставка
 * трактуется как 0.
 */
export const VAT_RATE_NONE = -1;
export const VAT_RATE_OPTIONS = [
    { value: '-1', label: 'Без НДС' },
    { value: '0', label: '0%' },
    { value: '5', label: '5%' },
    { value: '7', label: '7%' },
    { value: '10', label: '10%' },
    { value: '20', label: '20%' },
    { value: '22', label: '22%' },
];
/** Подпись ставки НДС: -1 → «Без НДС», иначе «N%». */
export function vatRateLabel(rate) {
    return rate === VAT_RATE_NONE ? 'Без НДС' : `${rate}%`;
}
/** Позиция котировки (услуга): своя цена (без НДС) и своя ставка НДС. */
export const quoteLineSchema = z.object({
    /** Код услуги из справочника (если из списка), иначе пусто. */
    code: z.string().max(100).optional(),
    /** Наименование услуги. */
    name: z.string().min(1).max(300),
    /** Цена без НДС. */
    amountNet: z.number().nonnegative(),
    /** Ставка НДС в процентах (0, 5, 10, 20, 22). */
    vatRate: z.number().min(-1).max(100),
});
/** Создание котировки менеджером — список позиций. */
export const quoteCreateSchema = z.object({
    /** ISO 4217 — RUB, USD, EUR и т.п. Принимаем upperCase 3-х буквенный код. */
    currency: z
        .string()
        .length(3)
        .regex(/^[A-Z]{3}$/, 'currency должен быть 3-х буквенным ISO 4217 кодом'),
    /** Позиции котировки (минимум одна). Сумма считается на сервере. */
    lines: z.array(quoteLineSchema).min(1),
    /** Срок действия предложения (ISO дата или datetime). */
    validUntil: z.string(),
    /** Примечание (опц.) — основное в позициях. */
    workScope: z.string().max(5000).optional(),
});
/** DTO котировки для клиента/менеджера. */
export const quoteDtoSchema = z.object({
    id: z.string(),
    orderId: z.string(),
    managerId: z.string(),
    /** Итог с НДС. */
    amount: z.number(),
    /** Итог без НДС. */
    amountNet: z.number().nullable(),
    /** Итог НДС. */
    vatAmount: z.number().nullable(),
    currency: z.string(),
    /** Единая ставка НДС, если у всех позиций одна; иначе null. */
    vatRate: z.number().nullable(),
    lines: z.array(quoteLineSchema).nullable(),
    validUntil: z.string().datetime(),
    workScope: z.string(),
    status: quoteStatusSchema,
    createdAt: z.string().datetime(),
    decidedAt: z.string().datetime().nullable(),
});
/** Контракт — read-only. */
export const contractDtoSchema = z.object({
    id: z.string(),
    number: z.string(),
    orderId: z.string(),
    quoteId: z.string(),
    signedAt: z.string().datetime(),
    endedAt: z.string().datetime().nullable(),
    subject: z.string(),
});
/** Расширенная карточка контракта со связями. */
export const contractDetailSchema = contractDtoSchema.extend({
    order: z.object({
        id: z.string(),
        number: z.string(),
        cargoName: z.string(),
        cargoWeightKg: z.number(),
        clientId: z.string(),
        clientDisplayName: z.string().nullable().optional(),
    }),
    quote: z
        .object({
        amount: z.number(),
        currency: z.string(),
        workScope: z.string(),
    })
        .nullable(),
    parties: z.array(z.object({
        id: z.string(),
        role: z.string(),
        notes: z.string().nullable(),
        carrier: z
            .object({
            id: z.string(),
            fullName: z.string(),
            inn: z.string().nullable(),
        })
            .nullable(),
    })),
});
//# sourceMappingURL=quotes.js.map